/**
 * The strategic Reach subsystem (plan I2; spec §10.1): tells the strategic core when the player leaves and enters
 * a system, and forwards relay reboots (`relays.ts`). Discovered from `src/<dir>/subsystem.ts` (M3). Order 110 runs
 * it before travel (200), so the hand-off is queued before the next system loads.
 *
 * The Reach runtime (and so this subsystem) exists before the strategic client: boot binds the client afterwards
 * with `bindStrategicLink` (service.ts), and until then the hooks do nothing. Every hand-off goes through one
 * serial promise chain (`queueHandoff`, shared with the load and catch-up flows), so an exit never overtakes the
 * enter before it; errors are logged, never thrown into the Reach step.
 */
import { logger } from '../core/logger'
import type { ReachSubsystem } from '../reach/runtime/types'
import type { StrategicClient } from '../strategic/client'
import { createRelaySync } from './relays'

/** The client calls the subsystem makes. */
export type LinkClient = Pick<StrategicClient, 'enterSystem' | 'exitSystem' | 'command'>

let bound: LinkClient | null = null
let chain: Promise<void> = Promise.resolve()
const relaySync = createRelaySync()
let skipRelayBacklog = false

function logHandoffError(err: unknown): void {
  logger.error('strategic link: system hand-off failed', err)
}

/** Binds (or unbinds) the client the subsystem drives. Reach events from before the bind are skipped. */
export function bindStrategicLink(client: LinkClient | null): void {
  bound = client
  skipRelayBacklog = client !== null
}

/** Runs `task` after every hand-off queued before it; resolves when it ran (errors are logged, not rethrown). */
export function queueHandoff(task: () => Promise<unknown>): Promise<void> {
  chain = chain.then(task).then(
    () => undefined,
    (err: unknown) => logHandoffError(err),
  )
  return chain
}

/** Resets the module state (tests). */
export function resetStrategicLink(): void {
  bound = null
  chain = Promise.resolve()
  skipRelayBacklog = false
}

export default function createStrategicSubsystem(): ReachSubsystem {
  return {
    name: 'strategic',
    order: 110,
    init(): void {},
    step(core): void {
      const client = bound
      if (client === null) return
      if (skipRelayBacklog) {
        relaySync.skip(core.events)
        skipRelayBacklog = false
        return
      }
      relaySync.step(core.events, core.galaxy, client)
    },
    onSystemLeave(): void {
      const client = bound
      if (client === null) return
      void queueHandoff(() => client.exitSystem())
    },
    onSystemEnter(_core, _sim, layout): void {
      const client = bound
      if (client === null) return
      const systemId = layout.systemId
      void queueHandoff(() => client.enterSystem(systemId))
    },
  }
}
